import {Injectable} from "@angular/core";
import {TransportOrder} from "../../pb-screen/models/transport-order/transport-order.interface";
import {environment} from "../../../environments/environment";
import {map, catchError, concatMap, toArray} from "rxjs/operators";
import {throwError as observableThrowError, Observable, forkJoin, of, from} from "rxjs";
import {HttpClientService} from "./httpclient";
import {MsalService} from "../../msal";
import {PlanningBlock} from "../../pb-screen/models/planning-block/planning-block.interface";
import {PLANNED, PRE_PLANNED, TO_BE_PLANNED} from "../../pb-screen/models/status-type.interface";
import {OrderLine} from "../../pb-screen/models/order-line/orderLine.interface";
import {ErrorHttpParams} from "./errror.httpparams";
import {DevLoggerService} from "./dev-logger.service";

@Injectable({
  providedIn: "root"
})
export class PlanningActionService {

  constructor(private readonly http: HttpClientService,
              private readonly authService: MsalService,
              private readonly devLogger: DevLoggerService) {
  }

  public sendTo(transportOrders: TransportOrder[]): Observable<TransportOrder[]> {
    let toIds = transportOrders.map(transportOrder => transportOrder.id).join(",");
    const account = this.authService.getAccount();
    return this.http
      .get(environment.tmsApiEndpointAddress + "transportorders/send-to-haulier?toId=" + toIds + "&userName=" + account.username)
      .pipe(
        map((response: any) => response.data),
        catchError((err) => observableThrowError(err))
      );
  }

  public cancelTo(transportOrders: TransportOrder[]): Observable<TransportOrder[]> {
    const requests = transportOrders.map(transportOrder =>
      this.http
        .patch(environment.tmsApiEndpointAddress + "transportorders/cancel-sent-to?toNumber=" + encodeURIComponent(transportOrder.id), null)
        .pipe(
          map((res: any) => res.data),
          catchError((err) => observableThrowError(err))
        )
    );
    return forkJoin(requests);
  }

  public unplanTo(transportOrders: TransportOrder[]): Observable<TransportOrder[]> {
    this.devLogger.planning(`unplanTo called for ${transportOrders.length} TOs`, transportOrders.map(t => t.id));
    const requests = transportOrders.map(transportOrder =>
      this.http
        .delete(environment.tmsApiEndpointAddress + "transportorders/id?toId=" + transportOrder.id) // + "&capacityId=" + capacityId)
        .pipe(
          map((res: any) => {
            this.devLogger.planning(`unplanTo success for TO: ${transportOrder.id}`);
            return res.data;
          }),
          catchError((error) => {
            this.devLogger.error('PlanningActionService', `unplanTo error for TO: ${transportOrder.id}`, error);
            return observableThrowError(error);
          })
        )
    );

    return forkJoin(requests);
  }

  public resendTo(transportOrders: TransportOrder[]): Observable<TransportOrder[]> {
    const account = this.authService.getAccount();
    const requests = transportOrders.map(transportOrder =>
      this.http
        .get(environment.tmsApiEndpointAddress + "transportorders/ResendID?transportorderid=" + transportOrder.id + "&userName=" + account.username)
        .pipe(
          map((res: any) => res.data),
          catchError((err: any) => observableThrowError(err))
        )
    );

    let results = forkJoin(requests).pipe(
      map((results: TransportOrder[][]) => results.reduce((acc, curr) => acc.concat(curr), []))
    );
    return results;
  }

  public sendNextPb(transportOrders: TransportOrder[]): Observable<PlanningBlock[]> {
    const account = this.authService.getAccount();

    if (!transportOrders || transportOrders.length === 0) {
      return of([] as PlanningBlock[]);
    }
    const requests: Observable<PlanningBlock[]>[] = transportOrders.map(transportOrder =>
      this.http
        .patch(
          environment.tmsApiEndpointAddress + "planningblocks/update-for-to/" + transportOrder.id + "/nextPb",
          {Status: {statusType: PLANNED}, UserName: account.username}
        )
        .pipe(
          map((res: any) => res.data as PlanningBlock[]),
          catchError((error) => observableThrowError(error))
        )
    );
    return forkJoin(requests).pipe(
      map((results: PlanningBlock[][]) => results.reduce((acc: PlanningBlock[], curr: PlanningBlock[]) => acc.concat(curr), []))
    );
  }

  public removeCapacityFromTo(transportOrders: TransportOrder[]): Observable<TransportOrder[]> {
    const account = this.authService.getAccount();
    const requests = transportOrders.map(transportOrder =>
      this.http
        .patch(environment.tmsApiEndpointAddress + "transportorders/remove-capacity-from-to", transportOrder)
        .pipe(
          map((response: any) => response.data),
          catchError((error) => observableThrowError(error))
        )
    );
    return forkJoin(requests);
  }

  public sendPb(planningBlocks: PlanningBlock[]): Observable<any> {
    const account = this.authService.getAccount();
    let planningBlocksDelta = [];
    planningBlocks.forEach((element) => {
      planningBlocksDelta.push({Id: element.id, Status: {"statusType": PLANNED}, TransportOrderNumber: element.transportOrderNumber});
    });
    return this.http.patch(environment.tmsApiEndpointAddress + "planningblocks", {
      PlanningBlocks: planningBlocksDelta,
      userName: account.username
    }).pipe(
      map((res: any) => {
        return res.data;
      }),
      catchError((err) => {
        return observableThrowError(err);
      })
    );
  }

  public cancelPb(planningBlocks: PlanningBlock[]): Observable<any> {
    const account = this.authService.getAccount();
    const planningBlocksDelta = [];
    planningBlocks.forEach((element) => {
      planningBlocksDelta.push({Id: element.id, Status: {"statusType": PRE_PLANNED}, TransportOrderNumber: element.transportOrderNumber});
    });
    return this.http.patch(environment.tmsApiEndpointAddress + "planningblocks", {
      PlanningBlocks: planningBlocksDelta,
      userName: account.username
    }).pipe(
      map((res: any) => {
        return res.data;
      }),
      catchError((err) => {
        return observableThrowError(err);
      })
    );
  }

  public unplanPb(planningBlocks: PlanningBlock[]): Observable<any> {
    const account = this.authService.getAccount();
    const planningBlocksDelta = [];
    planningBlocks.forEach((element) => {
      planningBlocksDelta.push({Id: element.id, Status: {"statusType": TO_BE_PLANNED}, TransportOrderNumber: element.transportOrderNumber});
    });
    return this.http.patch(environment.tmsApiEndpointAddress + "planningblocks", {
      PlanningBlocks: planningBlocksDelta,
      userName: account.username
    }).pipe(
      map((res: any) => {
        return res.data;
      }),
      catchError((err) => {
        return observableThrowError(err);
      })
    );
  }

  public replanPb(toId: string, pbs: PlanningBlock[]):
    Observable<PlanningBlock[]> {
    return from(pbs).pipe(
      concatMap(pb =>
        this.unplanPb([pb]).pipe(
          concatMap((unplannedPbs: PlanningBlock[]) => {
            const planningBlockToPlan = unplannedPbs?.length ? unplannedPbs[0] : pb;
            return this.http
              .post(environment.tmsApiEndpointAddress + "planningblocks/" + planningBlockToPlan.id + "/movePb/" + toId, {})
              .pipe(
                map((response: any) => response.data as PlanningBlock),
                catchError((error) => observableThrowError(error))
              );
          })
        )
      ),
      toArray()
    );
  }

  public getTransportOrderById(transportOrderId: string): Observable<TransportOrder | null> {
    const url = environment.tmsApiEndpointAddress +
      `transportorders/GetID?transportorderid=${transportOrderId}&WithPlanningBlocks=true`;

    // Option A: use getWithParams
    return this.http
      .getWithParams(url, new ErrorHttpParams(true))
      .pipe(
        map((response: any) => response?.data as TransportOrder),
        catchError((_err) => of(null)) // return an Observable that emits null
      );

  }
}
